import { Badge, Grid, Text } from '@mantine/core';
import { formatCodeableConcept } from '@medplum/core';
import { Resource } from '@medplum/fhirtypes';
import { useMemo } from 'react';
import { useLiveGraphQL } from '@/hooks/useLiveGraphQL';
import { useTimelineContext } from '../hooks/useTimelineContext';
import { PATIENT_ALLERGIES_QUERY, AllergyIntoleranceFragment } from '../graphql';
import { ListInput } from './shared/ListInput';
import { ListStateMessage } from './shared/ListStateMessage';
import { usePatientQueryVariables } from './shared/usePatientQueryVariables';
import { ORBIT_GRAY } from '@/theme/colors';

interface AllergyItem {
  id: string;
  value?: string;
  allergy: AllergyIntoleranceFragment;
}

const STATUS_CONFIG = {
  active: { label: 'ACTIVE', color: 'red' as const },
  resolved: { label: 'RESOLVED', color: 'green' as const },
  inactive: { label: 'INACTIVE', color: 'gray' as const },
} as const;

function formatSeverity(severity?: string): string {
  if (!severity) return '';
  return severity.charAt(0).toUpperCase() + severity.slice(1);
}

function getAllergenName(allergy: AllergyIntoleranceFragment): string {
  return allergy.code?.text || formatCodeableConcept(allergy.code) || 'Unknown allergen';
}

function AllergyStatusBadge({ statusCode }: { statusCode?: string }) {
  if (!statusCode) return null;

  const config = STATUS_CONFIG[statusCode as keyof typeof STATUS_CONFIG];
  if (!config) return null;

  return (
    <Badge size="xs" color={config.color} variant="light" style={{ textTransform: 'uppercase', fontWeight: 600 }}>
      {config.label}
    </Badge>
  );
}

function AllergyItemDisplay({ item }: { item: AllergyItem }) {
  const allergenName = getAllergenName(item.allergy);
  const clinicalStatusCode = item.allergy.clinicalStatus?.coding?.[0]?.code;
  const severity = formatSeverity(item.allergy.reaction?.[0]?.severity);

  return (
    <Grid align="center" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      <Grid.Col span={8} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Text
          truncate="end"
          style={{ fontSize: 14, lineHeight: '20px', fontWeight: 400, color: 'var(--mantine-color-black)' }}
        >
          {allergenName}
        </Text>
        <AllergyStatusBadge statusCode={clinicalStatusCode} />
      </Grid.Col>
      <Grid.Col span={4} style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {severity && (
          <Text style={{ fontSize: 14, lineHeight: '20px', fontWeight: 400, color: 'var(--mantine-color-black)' }}>
            {severity}
          </Text>
        )}
      </Grid.Col>
    </Grid>
  );
}

export function AllergiesList() {
  const { patientContext } = useTimelineContext();
  const patient = patientContext?.patient;
  const queryVariables = usePatientQueryVariables(patient);

  const {
    data: allergies,
    loading,
    error,
  } = useLiveGraphQL<AllergyIntoleranceFragment & Resource>(PATIENT_ALLERGIES_QUERY, queryVariables, {
    pageSize: 100,
  });

  const allergyItems: AllergyItem[] = useMemo(() => {
    if (!allergies) return [];
    return allergies.map((allergy) => ({
      id: allergy.id || '',
      value: getAllergenName(allergy),
      allergy,
    }));
  }, [allergies]);

  if (!patient) {
    return (
      <Text size="sm" c={ORBIT_GRAY}>
        No patient selected
      </Text>
    );
  }

  if (loading || error || allergyItems.length === 0) {
    return (
      <ListStateMessage
        patient={patient}
        loading={loading}
        error={error}
        empty={allergyItems.length === 0}
        emptyMessage="No allergies recorded"
        errorMessagePrefix="Error loading allergies"
      />
    );
  }

  return (
    <ListInput
      items={allergyItems}
      renderItem={(item) => <AllergyItemDisplay item={item} />}
      addForm={<div />}
      popoverOpened={false}
      setPopoverOpened={() => {}}
    />
  );
}
